import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Braces, Sparkles, Check, RotateCcw, Loader2 } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { TYPE_BADGE_COLORS } from './constants';

export default function PromptExpandModal({
  isOpen,
  onClose,
  label,
  value,
  onChange,
  placeholder,
  upstreamNodes = [],
  promptType = 'user', // 'system' or 'user'
}) {
  const [localValue, setLocalValue] = useState(value || '');
  const [refOpen, setRefOpen] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [enhancedPrompt, setEnhancedPrompt] = useState(null);
  const [enhanceError, setEnhanceError] = useState(null);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const refButtonRef = useRef(null);

  // Sync local value when modal opens or external value changes
  useEffect(() => {
    if (isOpen) {
      setLocalValue(value || '');
      setEnhancedPrompt(null);
      setEnhanceError(null);
    }
  }, [isOpen, value]);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      requestAnimationFrame(() => {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      });
    }
  }, [isOpen]);

  const handleSave = useCallback(() => {
    onChange(localValue);
    onClose();
  }, [localValue, onChange, onClose]);

  const handleDiscard = useCallback(() => {
    onClose();
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (refOpen) {
          setRefOpen(false);
        } else if (enhancedPrompt) {
          setEnhancedPrompt(null);
        } else {
          handleDiscard();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, refOpen, enhancedPrompt, handleDiscard]);

  // Close ref dropdown on click outside
  useEffect(() => {
    if (!refOpen) return;
    function handleClick(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        refButtonRef.current && !refButtonRef.current.contains(e.target)
      ) {
        setRefOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [refOpen]);

  const insertReference = useCallback((nodeId, path) => {
    const ref = `{{${nodeId}.${path}}}`;
    const el = textareaRef.current;
    if (!el) {
      setLocalValue((prev) => (prev || '') + ref);
      setRefOpen(false);
      return;
    }
    const start = el.selectionStart ?? (localValue || '').length;
    const end = el.selectionEnd ?? start;
    const before = (localValue || '').slice(0, start);
    const after = (localValue || '').slice(end);
    setLocalValue(before + ref + after);
    setRefOpen(false);
    requestAnimationFrame(() => {
      const pos = start + ref.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }, [localValue]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleDiscard();
  };

  const handleEnhance = async () => {
    if (!localValue.trim()) return;
    setEnhancing(true);
    setEnhanceError(null);
    try {
      const res = await apiRequest('/chat/enhance-prompt', {
        method: 'POST',
        body: JSON.stringify({
          prompt: localValue,
          prompt_type: promptType,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to enhance prompt');
      }
      const data = await res.json();
      setEnhancedPrompt(data.enhanced_prompt);
    } catch (err) {
      setEnhanceError(err.message || 'Something went wrong');
    } finally {
      setEnhancing(false);
    }
  };

  const handleAcceptEnhanced = () => {
    setLocalValue(enhancedPrompt);
    setEnhancedPrompt(null);
  };

  const handleDiscardEnhanced = () => {
    setEnhancedPrompt(null);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">{label}</h2>
            <span className="text-[10px] text-gray-400">
              Edit in expanded view
            </span>
          </div>

          {/* Reference picker button */}
          <div className="relative">
            <button
              ref={refButtonRef}
              type="button"
              onClick={() => setRefOpen((prev) => !prev)}
              className={`p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ${refOpen ? 'text-gray-600 bg-gray-100' : ''}`}
              title="Insert node reference"
            >
              <Braces className="w-4 h-4" />
            </button>

            {refOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
              >
                {upstreamNodes.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-gray-400 italic text-center">
                    No upstream nodes connected
                  </p>
                ) : (
                  upstreamNodes.map((node) => {
                    const Icon = node.icon;
                    return (
                      <div key={node.id}>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                          <div
                            className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${node.color}20` }}
                          >
                            <Icon className="w-2.5 h-2.5" style={{ color: node.color }} strokeWidth={2.5} />
                          </div>
                          <span className="text-[11px] font-medium text-gray-700 truncate">{node.label}</span>
                        </div>
                        {node.outputSchema.map((output) => (
                          <button
                            key={`${node.id}-${output.path}`}
                            type="button"
                            onClick={() => insertReference(node.id, output.path)}
                            className="w-full text-left px-3 py-1.5 pl-7 flex items-center gap-2 hover:bg-gray-50 transition-colors group"
                          >
                            <span className="text-xs text-gray-700 group-hover:text-gray-900 flex-1 truncate">
                              {output.label}
                            </span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${TYPE_BADGE_COLORS[output.type] || TYPE_BADGE_COLORS.string}`}>
                              {output.type}
                            </span>
                          </button>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <button
            onClick={handleDiscard}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            title="Close without saving"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Main editor area */}
        <div className="flex-1 overflow-hidden flex flex-col px-5 py-4 gap-3">
          <textarea
            ref={textareaRef}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder={placeholder}
            className="flex-1 w-full px-3 py-2.5 text-sm text-gray-800 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 placeholder:text-gray-300 transition-colors resize-none font-mono leading-relaxed"
            style={{ minHeight: '240px' }}
          />

          {/* Enhanced prompt comparison */}
          {enhancedPrompt && (
            <div className="border border-violet-200 rounded-lg bg-violet-50/50 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-violet-200 bg-violet-50">
                <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                <span className="text-xs font-medium text-violet-700">Enhanced version</span>
              </div>
              <div className="px-3 py-2.5 max-h-48 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                  {enhancedPrompt}
                </p>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 border-t border-violet-200 bg-violet-50/80">
                <button
                  onClick={handleAcceptEnhanced}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  Accept
                </button>
                <button
                  onClick={handleDiscardEnhanced}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Discard
                </button>
              </div>
            </div>
          )}

          {/* Enhance error */}
          {enhanceError && (
            <div className="px-3 py-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg">
              {enhanceError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={handleEnhance}
            disabled={enhancing || !localValue.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enhancing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {enhancing ? 'Enhancing...' : 'Enhance with AI'}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
